import { Suspense } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useSSR } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './legacyfrontend/i18n';
import './styles/index.css';
import storeClient from './storeClient';
import PreviewRoute from './frontend/features/preview/PreviewRoute';
import ContenidoPreviewRoute from './frontend/features/contenido/ContenidoPreviewRoute';

// The preview route is a pure renderer with no agency/tenant data and must never
// trigger tenant-sync or other API calls that <App /> fires unconditionally on mount.
// It is mounted directly here, bypassing Provider/BrowserRouter/App.
const isContenidoPreview = window.location.pathname === '/_preview/contenido';
const isPreviewRoute = window.location.pathname === '/_preview' || isContenidoPreview;

// Extend HTMLElement for React root container
interface ExtendedHTMLElement extends HTMLElement {
  _reactRootContainer?: Root;
}

const BaseApp = (): JSX.Element => {
  useSSR(window.initialI18nStore, window.initialLanguage);

  return (
    <Suspense fallback={<div />}>
      <HelmetProvider>
        <Provider store={storeClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Provider>
      </HelmetProvider>
    </Suspense>
  );
};

// Normalize proxy-rewritten root URLs before mounting the client router.
// `/index.php` is a legacy/proxy artifact and must not carry route params.
if (
  window.location.pathname === '/index.php' ||
  window.location.pathname.startsWith('/index.php/')
) {
  window.history.replaceState(null, '', '/');
}

const container = document.getElementById('root') as ExtendedHTMLElement;
let root: Root = container._reactRootContainer;
if (!root) {
  root = createRoot(container);
  container._reactRootContainer = root;
}
root.render(
  isContenidoPreview ? <ContenidoPreviewRoute /> : isPreviewRoute ? <PreviewRoute /> : <BaseApp />,
);

if (module.hot) {
  module.hot.accept();
}
